import React, { useEffect, useState, useCallback, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import {
    AreaChart,
    Area,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    ResponsiveContainer,
    PieChart,
    Pie,
    Cell,
    BarChart,
    Bar,
    Legend,
} from "recharts";
import {
    FiBook,
    FiUsers,
    FiUser,
    FiDollarSign,
    FiFolder,
    FiUserCheck,
    FiRefreshCw,
    FiActivity,
    FiTrendingUp,
    FiTrendingDown,
    FiArrowRight,
    FiCalendar,
    FiCheckCircle,
    FiClock,
    FiAward,
    FiTarget,
} from "react-icons/fi";
import { selectUserInformations } from "../../features/authSlice";
import { API } from "../../shared/api/Api";
import { showToast } from "../../utils/utils";
import Loader from "../../components/Live/Loader";
import OnlineUsers from "../../components/Statistics/OnlineUsers";
import {
    selectDashboardStats,
    selectOnlineUsers,
    setStats,
} from "../../features/dashboardSlice";
import { useNavigate, Link } from "react-router-dom";

// Animation variants
const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
        opacity: 1,
        transition: { staggerChildren: 0.08, delayChildren: 0.2 },
    },
};

const cardVariants = {
    hidden: { opacity: 0, y: 20, scale: 0.95 },
    visible: {
        opacity: 1,
        y: 0,
        scale: 1,
        transition: { type: "spring", stiffness: 100, damping: 15 },
    },
};

const fadeIn = {
    hidden: { opacity: 0, y: -10 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.5 } },
};

// Chart colors
const COLORS = ['#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6', '#EC4899'];
const GRADIENT_COLORS = {
    blue: ['#3B82F6', '#1D4ED8'],
    green: ['#10B981', '#059669'],
    orange: ['#F59E0B', '#D97706'],
    red: ['#EF4444', '#DC2626'],
    purple: ['#8B5CF6', '#7C3AED'],
};

// Modern Stat Card Component
const ModernStatCard = ({ title, value, icon: Icon, color, trend, trendValue, to, children }) => {
    const navigate = useNavigate();
    const colorClasses = {
        blue: 'from-blue-500 to-blue-600',
        green: 'from-emerald-500 to-emerald-600',
        orange: 'from-orange-500 to-orange-600',
        purple: 'from-purple-500 to-purple-600',
        red: 'from-red-500 to-red-600',
        indigo: 'from-indigo-500 to-indigo-600',
    };

    return (
        <motion.div
            whileHover={{ y: -5, transition: { duration: 0.2 } }}
            className="bg-white rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 overflow-hidden cursor-pointer group"
            onClick={() => to && navigate(to)}
        >
            <div className={`h-1.5 bg-gradient-to-r ${colorClasses[color]}`} />
            <div className="p-5">
                <div className="flex items-start justify-between mb-4">
                    <div className={`p-3 rounded-xl bg-gradient-to-br ${colorClasses[color]} shadow-lg`}>
                        <Icon className="w-6 h-6 text-white" />
                    </div>
                    {trend && (
                        <div className={`flex items-center gap-1 text-sm font-medium ${trend === 'up' ? 'text-emerald-500' : 'text-red-500'
                            }`}>
                            {trend === 'up' ? <FiTrendingUp className="w-4 h-4" /> : <FiTrendingDown className="w-4 h-4" />}
                            <span>{trendValue}%</span>
                        </div>
                    )}
                </div>
                <div className="space-y-1">
                    <h3 className="text-sm font-medium text-gray-500">{title}</h3>
                    <p className="text-3xl font-bold text-gray-900">{value?.toLocaleString() || 0}</p>
                </div>
                {children && (
                    <div className="mt-4 pt-4 border-t border-gray-100">
                        <div className="flex items-center justify-between text-sm">
                            {children}
                        </div>
                    </div>
                )}
                {to && (
                    <div className="mt-4 flex items-center text-sm font-medium text-gray-500 group-hover:text-blue-600 transition-colors">
                        <span>View Details</span>
                        <FiArrowRight className="w-4 h-4 ml-1 group-hover:translate-x-1 transition-transform" />
                    </div>
                )}
            </div>
        </motion.div>
    );
};

// Mini Stat Item
const MiniStat = ({ label, value, color }) => {
    const dotColors = {
        green: 'bg-emerald-500',
        red: 'bg-red-500',
        yellow: 'bg-yellow-500',
        blue: 'bg-blue-500',
        gray: 'bg-gray-500',
    };

    return (
        <div className="flex items-center gap-2">
            <span className={`w-2 h-2 rounded-full ${dotColors[color]}`} />
            <span className="text-gray-500">{label}:</span>
            <span className="font-semibold text-gray-900">{value}</span>
        </div>
    );
};

// Progress Ring Component
const ProgressRing = ({ progress, size = 80, strokeWidth = 8, color }) => {
    const radius = (size - strokeWidth) / 2;
    const circumference = radius * 2 * Math.PI;
    const strokeDashoffset = circumference - (progress / 100) * circumference;

    return (
        <div className="relative" style={{ width: size, height: size }}>
            <svg width={size} height={size} className="transform -rotate-90">
                <circle
                    cx={size / 2}
                    cy={size / 2}
                    r={radius}
                    stroke="currentColor"
                    strokeWidth={strokeWidth}
                    fill="none"
                    className="text-gray-200"
                />
                <circle
                    cx={size / 2}
                    cy={size / 2}
                    r={radius}
                    stroke={color}
                    strokeWidth={strokeWidth}
                    fill="none"
                    strokeDasharray={circumference}
                    strokeDashoffset={strokeDashoffset}
                    strokeLinecap="round"
                    className="transition-all duration-500"
                />
            </svg>
            <div className="absolute inset-0 flex items-center justify-center">
                <span className="text-lg font-bold text-gray-900">{Math.round(progress)}%</span>
            </div>
        </div>
    );
};

// Quick Action Card
const QuickActionCard = ({ icon: Icon, title, description, to, color }) => {
    const navigate = useNavigate();

    return (
        <motion.div
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            onClick={() => navigate(to)}
            className="flex items-center gap-4 p-4 bg-white rounded-xl shadow-sm hover:shadow-md transition-all cursor-pointer border border-gray-100"
        >
            <div className={`p-3 rounded-lg ${color}`}>
                <Icon className="w-5 h-5 text-white" />
            </div>
            <div className="flex-1">
                <h4 className="font-semibold text-gray-900">{title}</h4>
                <p className="text-sm text-gray-500">{description}</p>
            </div>
            <FiArrowRight className="w-5 h-5 text-gray-400" />
        </motion.div>
    );
};

const DashboardAdmin = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const user = useSelector(selectUserInformations);
    const [loader, setLoader] = useState(false);
    const [refreshing, setRefreshing] = useState(false);
    const [error, setError] = useState(null);
    const onlineUsers = useSelector(selectOnlineUsers);
    const stats = useSelector(selectDashboardStats);

    const fetchInitialData = useCallback(async (showLoader = true) => {
        if (showLoader) setLoader(true);
        else setRefreshing(true);
        setError(null);

        try {
            const { data } = await axios.get(
                API.Dashboard.app_statistics_countOfData,
                { headers: { Authorization: `Bearer ${user?.token}` } }
            );
            dispatch(setStats(data.data));
        } catch (error) {
            const errorMessage = error.response?.data?.message || "Failed to load statistics";
            showToast("Error", errorMessage, "error");
            setError(errorMessage);
        } finally {
            if (showLoader) setLoader(false);
            else setRefreshing(false);
        }
    }, [user?.token, dispatch]);

    useEffect(() => {
        if (user?.token) {
            fetchInitialData();
        }
    }, [user?.token, fetchInitialData]);

    const handleRefresh = () => {
        fetchInitialData(false);
    };

    // Prepare chart data
    const pieChartData = useMemo(() => {
        if (!stats) return [];
        return [
            { name: 'Courses', value: stats.courses?.total || 0 },
            { name: 'Students', value: stats.students?.total || 0 },
            { name: 'Instructors', value: stats.instructors?.total || 0 },
            { name: 'Materials', value: stats.materials || 0 },
        ];
    }, [stats]);

    const paymentChartData = useMemo(() => {
        if (!stats) return [];
        return [
            { name: 'Verified', value: stats.payments?.verified || 0, fill: '#10B981' },
            { name: 'Pending', value: stats.payments?.pending || 0, fill: '#F59E0B' },
        ];
    }, [stats]);

    const userStatusData = useMemo(() => {
        if (!stats) return [];
        return [
            { name: 'Active', value: stats.users?.active || 0, fill: '#10B981' },
            { name: 'Blocked', value: stats.users?.blocked || 0, fill: '#EF4444' },
        ];
    }, [stats]);

    // Weekly activity data from API (fallback to empty series)
    const activityData = useMemo(() => {
        if (stats?.weekly_activity && Array.isArray(stats.weekly_activity)) {
            return stats.weekly_activity.map((item) => ({
                name: item.name,
                enrollments: Number(item.enrollments) || 0,
                payments: Number(item.payments) || 0,
            }));
        }

        // Fallback: 7 days with zero values to keep chart stable
        const days = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
        return days.map((day) => ({
            name: day,
            enrollments: 0,
            payments: 0,
        }));
    }, [stats]);

    // Calculate percentages
    const paymentVerifiedPercent = useMemo(() => {
        if (!stats?.payments?.total) return 0;
        return Math.round((stats.payments.verified / stats.payments.total) * 100);
    }, [stats]);

    const studentActivePercent = useMemo(() => {
        if (!stats?.students?.total) return 0;
        return Math.round((stats.students.active / stats.students.total) * 100);
    }, [stats]);

    const getFormattedDate = () => {
        return new Date().toLocaleString('en-US', {
            month: 'short',
            day: 'numeric',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const getGreeting = () => {
        const hour = new Date().getHours();
        if (hour < 12) return 'Good Morning';
        if (hour < 18) return 'Good Afternoon';
        return 'Good Evening';
    };

    return (
        <div className="min-h-screen p-4 sm:p-6 lg:p-8">
            <div className="max-w-full mx-auto w-full">
                {/* Welcome Header */}
                <motion.div
                    variants={fadeIn}
                    initial="hidden"
                    animate="visible"
                    className="mb-8"
                >
                    <div className="bg-gradient-to-r from-blue-600 via-blue-700 to-indigo-700 rounded-3xl p-6 sm:p-8 shadow-xl overflow-hidden relative">
                        {/* Background Pattern */}
                        <div className="absolute inset-0 opacity-10">
                            <div className="absolute -right-10 -top-10 w-40 h-40 bg-white rounded-full" />
                            <div className="absolute -left-10 -bottom-10 w-60 h-60 bg-white rounded-full" />
                        </div>

                        <div className="relative z-10">
                            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6">
                                <div className="flex items-center gap-4">
                                    <div className="w-16 h-16 bg-white/20 backdrop-blur-sm rounded-2xl flex items-center justify-center">
                                        <FiUser className="w-8 h-8 text-white" />
                                    </div>
                                    <div>
                                        <p className="text-blue-100 text-sm font-medium">{getGreeting()}</p>
                                        <h1 className="text-2xl sm:text-3xl font-bold text-white">
                                            {user?.gender === "male" ? `Mr. ${user?.name}` : `Ms. ${user?.name}`}
                                        </h1>
                                        <p className="text-blue-200 text-sm mt-1">
                                            Here's what's happening with your platform today
                                        </p>
                                    </div>
                                </div>

                                <div className="flex items-center gap-3">
                                    <div className="text-right">
                                        <p className="text-blue-200 text-xs">Last updated</p>
                                        <p className="text-white text-sm font-medium">{getFormattedDate()}</p>
                                    </div>
                                    <button
                                        onClick={handleRefresh}
                                        disabled={refreshing || loader}
                                        className="p-3 bg-white/20 hover:bg-white/30 rounded-xl transition-all duration-200 disabled:opacity-50"
                                    >
                                        <FiRefreshCw className={`w-5 h-5 text-white ${refreshing ? 'animate-spin' : ''}`} />
                                    </button>
                                </div>
                            </div>

                            {/* Quick Stats in Header */}
                            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mt-6">
                                {[
                                    { icon: FiBook, label: 'Courses', value: stats?.courses?.total || 0, to: '/dashboard/courses' },
                                    { icon: FiUsers, label: 'Students', value: stats?.students?.total || 0, to: '/dashboard/students' },
                                    { icon: FiDollarSign, label: 'Payments', value: stats?.payments?.total || 0, to: '/dashboard/payments' },
                                    { icon: FiUserCheck, label: 'Users', value: stats?.users?.total || 0, to: '/dashboard/users' },
                                ].map((item, index) => (
                                    <div
                                        key={index}
                                        onClick={() => navigate(item.to)}
                                        className="bg-white/10 backdrop-blur-sm rounded-xl p-4 hover:bg-white/20 transition-all cursor-pointer group"
                                    >
                                        <div className="flex items-center gap-3">
                                            <item.icon className="w-5 h-5 text-white/80" />
                                            <span className="text-white/80 text-sm">{item.label}</span>
                                        </div>
                                        <p className="text-2xl font-bold text-white mt-2">{item.value.toLocaleString()}</p>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </motion.div>

                {/* Loading State */}
                <AnimatePresence>
                    {loader && (
                        <motion.div
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            className="flex justify-center items-center py-20"
                        >
                            <Loader />
                        </motion.div>
                    )}
                </AnimatePresence>

                {/* Error State */}
                {error && !loader && (
                    <motion.div
                        initial={{ opacity: 0, y: -10 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="mb-6 bg-red-50 border border-red-200 text-red-700 px-6 py-4 rounded-xl"
                    >
                        <div className="flex items-center gap-3">
                            <FiActivity className="w-5 h-5" />
                            <p className="font-medium">{error}</p>
                        </div>
                        <button
                            onClick={() => fetchInitialData()}
                            className="mt-3 text-sm text-red-600 hover:underline"
                        >
                            Try Again
                        </button>
                    </motion.div>
                )}

                {!loader && stats && (
                    <>
                        {/* Main Stats Grid */}
                        <motion.div
                            variants={containerVariants}
                            initial="hidden"
                            animate="visible"
                            className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5 mb-8"
                        >
                            <motion.div variants={cardVariants}>
                                <ModernStatCard
                                    title="Total Courses"
                                    value={stats.courses?.total}
                                    icon={FiBook}
                                    color="blue"
                                    to="/dashboard/courses"
                                >
                                    <MiniStat label="Active" value={stats.courses?.active} color="green" />
                                    <MiniStat label="Disabled" value={stats.courses?.disabled} color="gray" />
                                </ModernStatCard>
                            </motion.div>

                            <motion.div variants={cardVariants}>
                                <ModernStatCard
                                    title="Instructors"
                                    value={stats.instructors?.total}
                                    icon={FiUsers}
                                    color="purple"
                                    to="/dashboard/instructors"
                                >
                                    <MiniStat label="Active" value={stats.instructors?.active} color="green" />
                                    <MiniStat label="Away" value={stats.instructors?.disabled} color="yellow" />
                                </ModernStatCard>
                            </motion.div>

                            <motion.div variants={cardVariants}>
                                <ModernStatCard
                                    title="Students"
                                    value={stats.students?.total}
                                    icon={FiUser}
                                    color="green"
                                    to="/dashboard/students"
                                >
                                    <MiniStat label="Active" value={stats.students?.active} color="green" />
                                    <MiniStat label="Inactive" value={stats.students?.disabled} color="red" />
                                </ModernStatCard>
                            </motion.div>

                            <motion.div variants={cardVariants}>
                                <ModernStatCard
                                    title="Payments"
                                    value={stats.payments?.total}
                                    icon={FiDollarSign}
                                    color="orange"
                                    to="/dashboard/payments"
                                >
                                    <MiniStat label="Verified" value={stats.payments?.verified} color="green" />
                                    <MiniStat label="Pending" value={stats.payments?.pending} color="yellow" />
                                </ModernStatCard>
                            </motion.div>

                            <motion.div variants={cardVariants}>
                                <ModernStatCard
                                    title="Materials"
                                    value={stats.materials}
                                    icon={FiFolder}
                                    color="indigo"
                                    to="/dashboard/materials"
                                >
                                    <MiniStat label="Files" value={stats.materials} color="blue" />
                                </ModernStatCard>
                            </motion.div>

                            <motion.div variants={cardVariants}>
                                <ModernStatCard
                                    title="Users"
                                    value={stats.users?.total}
                                    icon={FiUserCheck}
                                    color="red"
                                    to="/dashboard/users"
                                >
                                    <MiniStat label="Active" value={stats.users?.active} color="green" />
                                    <MiniStat label="Blocked" value={stats.users?.blocked} color="red" />
                                </ModernStatCard>
                            </motion.div>
                        </motion.div>

                        {/* Charts Section */}
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
                            {/* Activity Chart */}
                            <motion.div
                                variants={cardVariants}
                                initial="hidden"
                                animate="visible"
                                className="bg-white rounded-2xl shadow-lg p-6"
                            >
                                <div className="flex items-center justify-between mb-6">
                                    <div>
                                        <h3 className="text-lg font-bold text-gray-900">Weekly Activity</h3>
                                        <p className="text-sm text-gray-500">Enrollments & Payments overview</p>
                                    </div>
                                    <div className="flex items-center gap-4">
                                        <div className="flex items-center gap-2">
                                            <span className="w-3 h-3 rounded-full bg-blue-500" />
                                            <span className="text-xs text-gray-500">Enrollments</span>
                                        </div>
                                        <div className="flex items-center gap-2">
                                            <span className="w-3 h-3 rounded-full bg-emerald-500" />
                                            <span className="text-xs text-gray-500">Payments</span>
                                        </div>
                                    </div>
                                </div>
                                <ResponsiveContainer width="100%" height={280}>
                                    <AreaChart data={activityData}>
                                        <defs>
                                            <linearGradient id="colorEnrollments" x1="0" y1="0" x2="0" y2="1">
                                                <stop offset="5%" stopColor="#3B82F6" stopOpacity={0.3} />
                                                <stop offset="95%" stopColor="#3B82F6" stopOpacity={0} />
                                            </linearGradient>
                                            <linearGradient id="colorPayments" x1="0" y1="0" x2="0" y2="1">
                                                <stop offset="5%" stopColor="#10B981" stopOpacity={0.3} />
                                                <stop offset="95%" stopColor="#10B981" stopOpacity={0} />
                                            </linearGradient>
                                        </defs>
                                        <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" vertical={false} />
                                        <XAxis dataKey="name" axisLine={false} tickLine={false} tick={{ fill: '#9CA3AF', fontSize: 12 }} />
                                        <YAxis axisLine={false} tickLine={false} tick={{ fill: '#9CA3AF', fontSize: 12 }} />
                                        <Tooltip
                                            contentStyle={{
                                                backgroundColor: '#1F2937',
                                                border: 'none',
                                                borderRadius: '8px',
                                                color: '#fff'
                                            }}
                                        />
                                        <Area type="monotone" dataKey="enrollments" stroke="#3B82F6" strokeWidth={2} fillOpacity={1} fill="url(#colorEnrollments)" />
                                        <Area type="monotone" dataKey="payments" stroke="#10B981" strokeWidth={2} fillOpacity={1} fill="url(#colorPayments)" />
                                    </AreaChart>
                                </ResponsiveContainer>
                            </motion.div>

                            {/* Distribution Chart */}
                            <motion.div
                                variants={cardVariants}
                                initial="hidden"
                                animate="visible"
                                className="bg-white rounded-2xl shadow-lg p-6"
                            >
                                <div className="flex items-center justify-between mb-6">
                                    <div>
                                        <h3 className="text-lg font-bold text-gray-900">Platform Distribution</h3>
                                        <p className="text-sm text-gray-500">Overview of all entities</p>
                                    </div>
                                </div>
                                <div className="flex items-center justify-center">
                                    <ResponsiveContainer width="100%" height={280}>
                                        <PieChart>
                                            <Pie
                                                data={pieChartData}
                                                cx="50%"
                                                cy="50%"
                                                innerRadius={60}
                                                outerRadius={100}
                                                paddingAngle={5}
                                                dataKey="value"
                                            >
                                                {pieChartData.map((entry, index) => (
                                                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                                                ))}
                                            </Pie>
                                            <Tooltip
                                                contentStyle={{
                                                    backgroundColor: '#134f98',
                                                    border: 'none',
                                                    borderRadius: '8px',
                                                    color: '#fff'
                                                }}
                                            />
                                            <Legend
                                                verticalAlign="bottom"
                                                height={36}
                                                formatter={(value) => <span className="text-gray-600 text-sm">{value}</span>}
                                            />
                                        </PieChart>
                                    </ResponsiveContainer>
                                </div>
                            </motion.div>
                        </div>

                        {/* Progress & Stats Row */}
                        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
                            {/* Payment Status */}
                            <motion.div
                                variants={cardVariants}
                                initial="hidden"
                                animate="visible"
                                className="bg-white rounded-2xl shadow-lg p-6"
                            >
                                <h3 className="text-lg font-bold text-gray-900 mb-6">Payment Status</h3>
                                <div className="flex items-center justify-between">
                                    <div className="space-y-4">
                                        <div>
                                            <div className="flex items-center gap-2 mb-1">
                                                <FiCheckCircle className="w-4 h-4 text-emerald-500" />
                                                <span className="text-sm text-gray-600">Verified</span>
                                            </div>
                                            <p className="text-2xl font-bold text-gray-900">{stats.payments?.verified || 0}</p>
                                        </div>
                                        <div>
                                            <div className="flex items-center gap-2 mb-1">
                                                <FiClock className="w-4 h-4 text-yellow-500" />
                                                <span className="text-sm text-gray-600">Pending</span>
                                            </div>
                                            <p className="text-2xl font-bold text-gray-900">{stats.payments?.pending || 0}</p>
                                        </div>
                                    </div>
                                    <ProgressRing progress={paymentVerifiedPercent} color="#10B981" />
                                </div>
                            </motion.div>

                            {/* Student Activity */}
                            <motion.div
                                variants={cardVariants}
                                initial="hidden"
                                animate="visible"
                                className="bg-white rounded-2xl shadow-lg p-6"
                            >
                                <h3 className="text-lg font-bold text-gray-900 mb-6">Student Activity</h3>
                                <div className="flex items-center justify-between">
                                    <div className="space-y-4">
                                        <div>
                                            <div className="flex items-center gap-2 mb-1">
                                                <FiTarget className="w-4 h-4 text-emerald-500" />
                                                <span className="text-sm text-gray-600">Active</span>
                                            </div>
                                            <p className="text-2xl font-bold text-gray-900">{stats.students?.active || 0}</p>
                                        </div>
                                        <div>
                                            <div className="flex items-center gap-2 mb-1">
                                                <FiUser className="w-4 h-4 text-gray-400" />
                                                <span className="text-sm text-gray-600">Inactive</span>
                                            </div>
                                            <p className="text-2xl font-bold text-gray-900">{stats.students?.disabled || 0}</p>
                                        </div>
                                    </div>
                                    <ProgressRing progress={studentActivePercent} color="#3B82F6" />
                                </div>
                            </motion.div>

                            {/* User Overview */}
                            <motion.div
                                variants={cardVariants}
                                initial="hidden"
                                animate="visible"
                                className="bg-white rounded-2xl shadow-lg p-6"
                            >
                                <div className="flex items-center justify-between mb-3">
                                    <h3 className="text-lg font-bold text-gray-900">User Status</h3>
                                    <div className="flex items-center gap-3 text-xs text-gray-500">
                                        <div className="flex items-center gap-1">
                                            <span className="w-2.5 h-2.5 rounded-full bg-emerald-500" />
                                            <span>Active</span>
                                        </div>
                                        <div className="flex items-center gap-1">
                                            <span className="w-2.5 h-2.5 rounded-full bg-red-500" />
                                            <span>Blocked</span>
                                        </div>
                                    </div>
                                </div>
                                <p className="text-xs text-gray-500 mb-4">
                                    Quick overview of active vs blocked accounts
                                </p>
                                <div className="bg-gray-50 rounded-xl px-3 py-2">
                                    <ResponsiveContainer width="100%" height={150}>
                                        <BarChart data={userStatusData} layout="vertical" barSize={18}>
                                            <XAxis type="number" hide />
                                            <YAxis
                                                type="category"
                                                dataKey="name"
                                                axisLine={false}
                                                tickLine={false}
                                                tick={{ fill: '#6B7280', fontSize: 12 }}
                                                width={70}
                                            />
                                            <CartesianGrid horizontal={false} stroke="#E5E7EB" />
                                            <Tooltip
                                                cursor={{ fill: 'rgba(59,130,246,0.08)' }}
                                                contentStyle={{
                                                    backgroundColor: '#FFFFFF',
                                                    borderRadius: 8,
                                                    border: '1px solid #E5E7EB',
                                                    fontSize: 12,
                                                    color: '#111827',
                                                }}
                                            />
                                            <Bar dataKey="value" radius={[0, 10, 10, 0]}>
                                                {userStatusData.map((entry, index) => (
                                                    <Cell
                                                        key={`cell-user-status-${index}`}
                                                        fill={entry.name === 'Active' ? '#10B981' : '#EF4444'}
                                                    />
                                                ))}
                                            </Bar>
                                        </BarChart>
                                    </ResponsiveContainer>
                                </div>
                                <div className="mt-4 pt-4 border-t border-gray-100 flex items-center justify-between">
                                    <div className="space-y-1">
                                        <p className="text-xs uppercase tracking-wide text-gray-400">
                                            Total Users
                                        </p>
                                        <p className="text-xl font-bold text-gray-900">
                                            {stats.users?.total?.toLocaleString() || 0}
                                        </p>
                                    </div>
                                    <div className="text-right space-y-1">
                                        <p className="text-xs text-gray-400">Active ratio</p>
                                        <p className="text-sm font-semibold text-emerald-600">
                                            {stats.users?.total
                                                ? `${Math.round((stats.users.active / stats.users.total) * 100)}% active`
                                                : '—'}
                                        </p>
                                    </div>
                                </div>
                            </motion.div>
                        </div>

                        {/* Quick Actions & Online Users */}
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                            {/* Quick Actions */}
                            <motion.div
                                variants={cardVariants}
                                initial="hidden"
                                animate="visible"
                                className="bg-white rounded-2xl shadow-lg p-6"
                            >
                                <h3 className="text-lg font-bold text-gray-900 mb-4">Quick Actions</h3>
                                <div className="space-y-3">
                                    <QuickActionCard
                                        icon={FiBook}
                                        title="Create Course"
                                        description="Add a new course to the platform"
                                        to="/dashboard/courses/create"
                                        color="bg-blue-500"
                                    />
                                    <QuickActionCard
                                        icon={FiUser}
                                        title="Add Student"
                                        description="Register a new student"
                                        to="/dashboard/students/create"
                                        color="bg-emerald-500"
                                    />
                                    <QuickActionCard
                                        icon={FiUsers}
                                        title="Add Instructor"
                                        description="Onboard a new instructor"
                                        to="/dashboard/instructors/create"
                                        color="bg-purple-500"
                                    />
                                    <QuickActionCard
                                        icon={FiActivity}
                                        title="Student History"
                                        description="View student academic records"
                                        to="/dashboard/students/history"
                                        color="bg-orange-500"
                                    />
                                </div>
                            </motion.div>

                            {/* Online Users */}
                            <motion.div
                                variants={cardVariants}
                                initial="hidden"
                                animate="visible"
                                className="bg-white rounded-2xl shadow-lg p-6"
                            >
                                <div className="flex items-center justify-between mb-4">
                                    <h3 className="text-lg font-bold text-gray-900">Online Users</h3>
                                    <span className="px-3 py-1 bg-emerald-100 text-emerald-600 text-sm font-medium rounded-full">
                                        {onlineUsers?.length || 0} online
                                    </span>
                                </div>
                                <OnlineUsers users={onlineUsers} />
                            </motion.div>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
};

export default DashboardAdmin;
